import type { Deal } from '@/types'
import { onlyDigits } from './utils'
import { toDateInputValue } from './legalDataUtils'
import { toDatetimeLocalValue } from './reuniaoMeetingUtils'

export type ContractDeliveryMode = 'pdf' | 'link'

export type ContractDataModalMode = 'gerar_contrato' | 'handover_complete'

const MIN_OBSERVACAO_LENGTH = 10

export type ContractDataForm = {
  responsavelAssinaturaNome: string
  responsavelAssinaturaEmail: string
  cnpj: string
  contatoNome: string
  telefone: string
  responsavelFinanceiroNome: string
  emailFinanceiro: string
  contractStartDate: string
  firstPaymentDate: string
  contractDeliveryMode: ContractDeliveryMode | ''
  dataFechamentoHandover: string
  observacoesHandover: string
}

export function emptyContractDataForm(): ContractDataForm {
  return {
    responsavelAssinaturaNome: '',
    responsavelAssinaturaEmail: '',
    cnpj: '',
    contatoNome: '',
    telefone: '',
    responsavelFinanceiroNome: '',
    emailFinanceiro: '',
    contractStartDate: '',
    firstPaymentDate: '',
    contractDeliveryMode: '',
    dataFechamentoHandover: '',
    observacoesHandover: '',
  }
}

export function isValidEmail(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
}

export function buildContractDataForm(
  deal: Pick<
    Deal,
    | 'contatoNome'
    | 'telefone'
    | 'representanteLegal'
    | 'emailFinanceiro'
    | 'email'
    | 'contractDeliveryMode'
    | 'contractStartDate'
    | 'contractStart'
    | 'firstPaymentDate'
    | 'dataFechamento'
    | 'observacoes'
  >,
  empresa?: Record<string, unknown> | null,
): ContractDataForm {
  const mode = deal.contractDeliveryMode
  return {
    responsavelAssinaturaNome: String(
      empresa?.responsavelAssinaturaNome ?? deal.representanteLegal ?? '',
    ).trim(),
    responsavelAssinaturaEmail: String(empresa?.responsavelAssinaturaEmail ?? deal.email ?? '').trim(),
    cnpj: String(empresa?.cnpj ?? '').trim(),
    contatoNome: String(empresa?.contato ?? deal.contatoNome ?? '').trim(),
    telefone: String(empresa?.telefone ?? deal.telefone ?? '').trim(),
    responsavelFinanceiroNome: String(empresa?.responsavelFinanceiroNome ?? '').trim(),
    emailFinanceiro: String(
      empresa?.responsavelFinanceiroEmail ?? deal.emailFinanceiro ?? '',
    ).trim(),
    contractStartDate: toDateInputValue(deal.contractStartDate ?? deal.contractStart),
    firstPaymentDate: toDateInputValue(deal.firstPaymentDate),
    contractDeliveryMode: mode === 'pdf' || mode === 'link' ? mode : '',
    dataFechamentoHandover: deal.dataFechamento
      ? toDatetimeLocalValue(deal.dataFechamento)
      : toDatetimeLocalValue(new Date().toISOString()),
    observacoesHandover: String(deal.observacoes ?? '').trim(),
  }
}

export function getMissingContractDataFields(form: ContractDataForm): string[] {
  const missing: string[] = []

  if (!form.responsavelAssinaturaNome.trim()) {
    missing.push('Assinante')
  }
  if (!isValidEmail(form.responsavelAssinaturaEmail)) {
    missing.push('E-mail do assinante')
  }
  if (onlyDigits(form.cnpj).length !== 14) {
    missing.push('CNPJ')
  }
  if (!form.contatoNome.trim()) {
    missing.push('Contato')
  }
  if (!form.telefone.trim()) {
    missing.push('WhatsApp')
  }
  if (!form.responsavelFinanceiroNome.trim()) {
    missing.push('Nome financeiro')
  }
  if (!isValidEmail(form.emailFinanceiro)) {
    missing.push('E-mail financeiro')
  }
  if (!form.contractStartDate.trim()) {
    missing.push('Data inicial do contrato')
  }
  if (!form.firstPaymentDate.trim()) {
    missing.push('Data inicial de pagamento')
  }
  if (form.contractDeliveryMode !== 'pdf' && form.contractDeliveryMode !== 'link') {
    missing.push('Formato do contrato')
  }

  return missing
}

export function getMissingHandoverCompletionFields(form: ContractDataForm): string[] {
  const missing: string[] = []
  if (!form.dataFechamentoHandover.trim()) {
    missing.push('Data do fechamento')
  }
  if (form.observacoesHandover.trim().length < MIN_OBSERVACAO_LENGTH) {
    missing.push('Observação do projeto vendido')
  }
  return missing
}

export function isHandoverCompletionComplete(form: ContractDataForm): boolean {
  return getMissingHandoverCompletionFields(form).length === 0
}

export function isContractDataComplete(form: ContractDataForm): boolean {
  return getMissingContractDataFields(form).length === 0
}

export function buildEmpresaContractPayload(form: ContractDataForm) {
  return {
    cnpj: onlyDigits(form.cnpj),
    contato: form.contatoNome.trim(),
    telefone: form.telefone.trim(),
    responsavelAssinaturaNome: form.responsavelAssinaturaNome.trim(),
    responsavelAssinaturaEmail: form.responsavelAssinaturaEmail.trim(),
    responsavelFinanceiroNome: form.responsavelFinanceiroNome.trim(),
    responsavelFinanceiroEmail: form.emailFinanceiro.trim(),
  }
}
